'use strict';

const MenusTypeEnum = {
    SPLASH_SCREEN: 'splash_screen',
    TWO_OPTION_LARGE: 'two_option_large',
    THREE_OPTION_LARGE: 'three_option_large',
    TWO_TO_SIX_OPTION_FULL_SCREEN: 'two_to_six_option_full_screen',
    TWO_TO_FOUR_OPTION_HALF_SCREEN: 'two_to_four_option_half_screen',
    INFINITE_OPTION: 'infinite_option'
};

class OverallMenuController {
    static currentMenuController = null;
    static currentMenuType = null;
    static creationPromise = null;
    static creationCancelled = false;

    static _managerMap = {
        [MenusTypeEnum.SPLASH_SCREEN]: {
            manager: MenuSplashScreenManager,
            createMethod: 'AddNewSplashScreen',
            removeMethod: 'RemoveAllSplashScreens'
        },
        [MenusTypeEnum.TWO_OPTION_LARGE]: {
            manager: MenuTwoOptionLargeManager,
            createMethod: 'AddNewTwoOptionLarge',
            removeMethod: 'RemoveAllTwoOptionLarge'
        },
        [MenusTypeEnum.THREE_OPTION_LARGE]: {
            manager: MenuThreeOptionLargeManager,
            createMethod: 'createMenu',
            removeMethod: 'RemoveAllThreeOptionLarge'
        },
        [MenusTypeEnum.TWO_TO_SIX_OPTION_FULL_SCREEN]: {
            manager: MenuTwoToSixOptionFullScreenManager,
            createMethod: 'createMenu',
            removeMethod: 'RemoveAllTwoToSixOptionFullScreen'
        },
        [MenusTypeEnum.TWO_TO_FOUR_OPTION_HALF_SCREEN]: {
            manager: MenuTwoToFourOptionHalfScreenManager,
            createMethod: 'AddNewTwoToFourOptionHalfScreen',
            removeMethod: 'RemoveAllTwoToFourOptionHalfScreen'
        },
        [MenusTypeEnum.INFINITE_OPTION]: {
            manager: MenuInfiniteOptionManager,
            createMethod: 'AddNewInfiniteOption',
            removeMethod: 'RemoveAllInfiniteOption'
        }
    };

    /**
     * Creates and displays a menu based on the provided configuration
     * @param {Object} menuConfig - Menu configuration object
     * @param {string} menuConfig.menuType - Type of menu to create
     * @param {Object} menuConfig.style - Style configuration (colors, opacity, etc.)
     * @param {Object} menuConfig.content - Content configuration (title, text, buttons, etc.)
     * @param {Object} menuConfig.scene - Scene positioning and properties
     */
    static async createMenu(menuConfig) {
        if (this.creationPromise) {
            this.creationCancelled = true;
            try {
                await this.creationPromise;
            } catch (error) {
                console.error('OverallMenuController: Error cancelling previous menu creation', error);
            }
        }

        this.removeCurrentMenu();

        this.creationCancelled = false;
        const creationPromise = this._createMenuInternal(menuConfig);
        this.creationPromise = creationPromise;
        
        try {
            const result = await creationPromise;
            if (this.creationPromise === creationPromise) {
                this._clearCreationState();
            }
            return result;
        } catch (error) {
            console.error('OverallMenuController: Error creating menu', { menuType: menuConfig?.menuType, error });
            if (this.creationPromise === creationPromise) {
                this._clearCreationState();
            }
            throw error;
        }
    }

    static _clearCreationState() {
        this.creationPromise = null;
        this.creationCancelled = false;
    }

    static async _createMenuInternal(menuConfig) {
        const { menuType, style, content, scene } = menuConfig;

        if (this.creationCancelled) {
            return null;
        }

        const config = this._managerMap[menuType];
        if (!config) {
            return null;
        }

        const menuManager = config.manager;
        if (!menuManager) {
            return null;
        }

        const props = {
            ...content,
            ...style,
            Scene: scene,
            type: menuType
        };

        const menuController = await menuManager[config.createMethod](props);

        if (this.creationCancelled) {
            this._cleanupMenu(menuController, menuType);
            return null;
        }

        this.currentMenuController = menuController;
        this.currentMenuType = menuType;
        
        if (menuController && typeof threeMeshMenuControllers !== 'undefined') {
            threeMeshMenuControllers.push(menuController);
        }
        
        return menuController;
    }

    static _cleanupMenu(menuController, menuType) {
        if (!menuController) {
            return;
        }

        try {
            menuController.dispose?.();
            this._removeMenuByType(menuType);
        } catch (error) {
            console.error(`OverallMenuController: Error cleaning up cancelled menu (type: ${menuType})`, error);
        }
    }

    static _removeMenuByType(menuType) {
        const config = this._managerMap[menuType];
        if (!config) {
            return;
        }

        try {
            const menuManager = config.manager;
            if (menuManager?.[config.removeMethod]) {
                menuManager[config.removeMethod]();
            }
        } catch (error) {
            console.error(`OverallMenuController: Error removing menu by type (${menuType})`, error);
        }
    }

    static removeCurrentMenu() {
        if (!this.currentMenuController || !this.currentMenuType) {
            return;
        }

        const menuType = this.currentMenuType;
        const menuController = this.currentMenuController;
        
        try {
            this._removeMenuByType(menuType);
        } catch (error) {
            console.error(`OverallMenuController: Error removing current menu (type: ${menuType})`, error);
        }

        if (typeof threeMeshMenuControllers !== 'undefined') {
            const index = threeMeshMenuControllers.findIndex(
                controller => controller === menuController || 
                (controller.id && menuController.id && controller.id === menuController.id)
            );
            if (index !== -1) {
                threeMeshMenuControllers.splice(index, 1);
            }
        }
        
        try {
            menuController.dispose?.();
        } catch (error) {
            console.error(`OverallMenuController: Error disposing menu controller (type: ${menuType})`, error);
        }

        if (this.currentMenuController === menuController) {
            this.currentMenuController = null;
            this.currentMenuType = null;
        }
    }

    static async onVRModeChange() {
        if (!this.currentMenuController || !this.currentMenuType) {
            return;
        }

        const config = this._managerMap[this.currentMenuType];
        if (!config) {
            return;
        }

        try {
            const menuManager = config.manager;
            if (menuManager?.onVRModeChange) {
                this.currentMenuController = await menuManager.onVRModeChange();
            }
        } catch (error) {
            console.error(`OverallMenuController: Error handling VR mode change (type: ${this.currentMenuType})`, error);
        }
    }

    static forceRemoveAllMenus() {
        if (this.creationPromise) {
            this.creationCancelled = true;
        }

        this.removeCurrentMenu();
        
        Object.values(this._managerMap).forEach(({ manager, removeMethod }) => {
            try {
                const menuManager = manager;
                if (menuManager?.[removeMethod]) {
                    menuManager[removeMethod]();
                }
            } catch (error) {
                console.error(`OverallMenuController: Error removing all menus (method: ${removeMethod})`, error);
            }
        });

        if (typeof threeMeshMenuControllers !== 'undefined') {
            threeMeshMenuControllers.length = 0;
        }
        
        this.currentMenuController = null;
        this.currentMenuType = null;
        this._clearCreationState();
    }
}

export { OverallMenuController, MenusTypeEnum };
export default OverallMenuController;
